// Context object + hook + types for the IntakeSubmitProvider. Kept
// in its own non-component file so the provider file (a .tsx) only
// exports components, satisfying react-refresh/only-export-components.
import { createContext, useContext } from "react";
import type { IntakeOrganization, NewProjectResult, TriageRecord } from "../api";

// Re-export under the local alias so callers can treat this file as
// the single import surface.
export type { IntakeOrganization, NewProjectResult, TriageRecord };

export interface IntakeSubmitInput {
  project_id: string;
  address: string;
  ask: string;
  mpans_csv: string;
  organizations: IntakeOrganization[];
  files: File[];
}

export type SubmitStage =
  | "idle"
  | "uploading"
  | "atlas"
  | "done"
  | "error";

export interface IntakeSubmitState {
  stage: SubmitStage;
  label: string;
  projectId: string | null;
  result: NewProjectResult | null;
  error: Error | null;
  uploadProgress: { current: number; total: number };
}

export interface IntakeSubmitContextValue {
  state: IntakeSubmitState;
  submit: (input: IntakeSubmitInput) => Promise<NewProjectResult>;
  dismiss: () => void;
}

export const IntakeSubmitContext =
  createContext<IntakeSubmitContextValue | null>(null);

export const useIntakeSubmitContext = (): IntakeSubmitContextValue => {
  const ctx = useContext(IntakeSubmitContext);
  if (!ctx) {
    throw new Error(
      "useIntakeSubmitContext must be used inside <IntakeSubmitProvider>",
    );
  }
  return ctx;
};
